/**
 * @fileoverview QuickTimeEntryDialog — log time from anywhere (MOR-906)
 *
 * Purpose: A single global dialog for recording a draft time entry without
 * navigating to the timesheets section. Mounted once in the dashboard
 * layout and opened via `useTimeTracker().openQuickEntry()` — from the
 * header button, the keyboard shortcut, or when a running timer is stopped.
 *
 * Behaviour worth knowing:
 * - When opened from a job route the matter is preselected, so logging time
 *   against the matter you are reading is one field away from done.
 * - Stopping the header timer opens this dialog with the units filled in and
 *   the measured start/end window carried across. Typed entries leave that
 *   window blank — it records when work happened, not how long it is billed.
 * - The plain-English composer (`AiQuickEntryDialog`) pre-fills every field
 *   here rather than saving directly, so a parse is always reviewed. Where it
 *   parsed several entries, saving one loads the next.
 * - "Save and add another" keeps the matter, category, date, billable and
 *   project code so consecutive entries are quick.
 *
 * Dependencies:
 * - Convex: timesheets.entries.entryContext, timesheets.entries.createEntry
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { convexQueryOrIdle } from "@/lib/convex-query-idle";
import { useMutation } from "convex/react";
import { Clock, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import {
  MAX_UNITS,
  MIN_UNITS,
  minutesToUnits,
  roundUnits,
} from "@cvx/timesheets/units";
import { addMinutesToClock } from "@cvx/timesheets/clock";

import { extractErrorMessage } from "@/utils/errors";
import { useActiveOrg } from "@/utils/org-context";
import { Button, Label } from "@/ui/primitives";
import { Input } from "@/ui/primitives/input";
import { Textarea } from "@/ui/primitives/textarea";
import { Spinner } from "@/ui/primitives/spinner";
import { Switch } from "@/ui/primitives/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/composed/overlays/dialog";
import { SearchableSelect } from "@/ui/composed/inputs/searchable-select";
import { Tabs, TabsList, TabsTrigger } from "@/ui/composed/sections/tabs";
import { DatePicker } from "@/ui/composed/inputs/date-picker";
import { TimePicker } from "@/ui/composed/inputs/time-picker";
import { DictationButton } from "@/ui/composed/inputs/dictation-button";
import { useDictatedField } from "@/ui/hooks/use-dictated-field";
import { CategoryGlyph } from "./CategoryBadge";
import { NARRATIVE_INPUT_PROPS } from "./narrativeCheck";
import { NarrativeWarningNote } from "./NarrativeWarningNote";
import { SubCategoryPicker } from "./SubCategoryPicker";
import { useTimeTracker } from "./TimeTrackerContext";

type ProjectCode = "pre" | "post";

/**
 * Matter time is logged against a job and billed; firm time (marketing, admin,
 * training) is logged against a firm-scoped activity, never rated or billed.
 */
type EntryMode = "matter" | "firm";

interface QuickEntryForm {
  mode: EntryMode;
  jobId: string;
  date: string;
  categoryId: string;
  subCategory: string;
  units: string;
  narrative: string;
  billable: boolean;
  projectCode: ProjectCode;
  /** HH:MM, or "" — the clock window is always optional (see below). */
  startTime: string;
  endTime: string;
}

function todayIso(): string {
  const now = new Date();
  const offsetMs = now.getTimezoneOffset() * 60_000;
  return new Date(now.getTime() - offsetMs).toISOString().slice(0, 10);
}

function emptyForm(): QuickEntryForm {
  return {
    mode: "matter",
    jobId: "",
    date: todayIso(),
    categoryId: "",
    subCategory: "",
    units: "",
    narrative: "",
    billable: true,
    projectCode: "post",
    startTime: "",
    endTime: "",
  };
}

export function QuickTimeEntryDialog() {
  const {
    quickEntryOpen,
    quickEntryPrefill,
    quickEntryRemaining,
    advanceQuickEntry,
    closeQuickEntry,
  } = useTimeTracker();
  const { activeOrg } = useActiveOrg();
  const orgId = activeOrg?._id;

  // The matter of the job page the user is on, if any.
  const routeParams = useParams({ strict: false }) as { jobId?: string };
  const routeJobId = routeParams.jobId;

  // `includeJobId` guarantees the matter being viewed is selectable, so
  // preselection still works on matters the default status filter drops.
  const { data: context } = useQuery({
    ...convexQueryOrIdle(
      api.timesheets.entries.entryContext,
      orgId
        ? {
            orgId,
            ...(routeJobId ? { includeJobId: routeJobId as Id<"jobs"> } : {}),
          }
        : undefined,
    ),
    enabled: !!orgId && quickEntryOpen,
  });

  const createEntry = useMutation(api.timesheets.entries.createEntry);

  const [form, setForm] = useState<QuickEntryForm>(emptyForm);
  const [showTimes, setShowTimes] = useState(false);
  // MOR-935: whether the person has typed a start or end, so a rolled-on
  // start or a derived end never overwrites something they chose.
  const [startTouched, setStartTouched] = useState(false);
  const [endTouched, setEndTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const narrativeRef = useRef<HTMLTextAreaElement>(null);
  const unitsRef = useRef<HTMLInputElement>(null);
  const parsedProjectCode = useRef<ProjectCode | null>(null);

  const unitMinutes = context?.unitMinutes ?? 6;

  // Reset to a clean form each time the dialog opens, then layer on the
  // prefill. Kept keyed on `quickEntryOpen` so reopening never inherits a
  // half-finished entry from last time. A new prefill object also arrives
  // when the queue advances, which reloads the form for the next entry.
  useEffect(() => {
    if (!quickEntryOpen) return;
    const empty = emptyForm();
    setForm({
      ...empty,
      jobId: quickEntryPrefill?.jobId ?? routeJobId ?? "",
      date: quickEntryPrefill?.date ?? empty.date,
      categoryId: quickEntryPrefill?.categoryId ?? "",
      units:
        quickEntryPrefill?.units !== undefined
          ? String(quickEntryPrefill.units)
          : "",
      narrative: quickEntryPrefill?.narrative ?? "",
      billable: quickEntryPrefill?.billable ?? empty.billable,
      projectCode: quickEntryPrefill?.projectCode ?? empty.projectCode,
      startTime: quickEntryPrefill?.startTime ?? "",
      endTime: quickEntryPrefill?.endTime ?? "",
    });
    // A measured window shows itself; otherwise the section stays out of the
    // way behind its own toggle.
    setShowTimes(
      !!(quickEntryPrefill?.startTime || quickEntryPrefill?.endTime),
    );
    // A measured window (the stopwatch) is the truth about when the work
    // happened; a rolled-on default is not, and may be replaced.
    setStartTouched(!!quickEntryPrefill?.startTime);
    setEndTouched(!!quickEntryPrefill?.endTime);
    // A project code the parser read out of the practitioner's own words
    // outranks the matter-phase default applied below.
    parsedProjectCode.current = quickEntryPrefill?.projectCode ?? null;
  }, [quickEntryOpen, quickEntryPrefill, routeJobId]);

  // Elapsed minutes only become units once the firm's unit size has loaded.
  useEffect(() => {
    const minutes = quickEntryPrefill?.elapsedMinutes;
    if (!quickEntryOpen || minutes === undefined || !context) return;
    const units = minutesToUnits(
      minutes,
      context.unitMinutes,
      context.roundingMode,
    );
    setForm((prev) => (prev.units ? prev : { ...prev, units: String(units) }));
  }, [quickEntryOpen, quickEntryPrefill, context]);

  // MOR-935: people who log against the clock get the times section open
  // and a start that rolls on from their last timed entry that day.
  const showTimeOfDay = context?.showTimeOfDay ?? false;
  useEffect(() => {
    if (quickEntryOpen && showTimeOfDay) setShowTimes(true);
  }, [quickEntryOpen, showTimeOfDay]);
  const { data: dayWindow } = useQuery({
    ...convexQueryOrIdle(
      api.timesheets.entries.dayWindow,
      orgId && form.date ? { orgId, date: form.date } : undefined,
    ),
    enabled: !!orgId && quickEntryOpen && showTimeOfDay && !!form.date,
  });
  // Two things this must never do (QA-50): replace the stopwatch's measured
  // start, and apply a window fetched for a different date. So it reads the
  // prefill directly rather than the `startTouched` flag (which is set in
  // another effect and is stale in the same commit), and checks the window's
  // own date against the form's.
  const measuredStart = quickEntryPrefill?.startTime;
  const rollOnStart =
    dayWindow && dayWindow.date === form.date
      ? dayWindow.nextStartTime
      : undefined;
  useEffect(() => {
    if (
      !quickEntryOpen ||
      !showTimeOfDay ||
      !rollOnStart ||
      measuredStart ||
      startTouched
    ) {
      return;
    }
    setForm((prev) =>
      prev.startTime === rollOnStart
        ? prev
        : { ...prev, startTime: rollOnStart },
    );
  }, [quickEntryOpen, showTimeOfDay, rollOnStart, measuredStart, startTouched]);

  const isFirm = form.mode === "firm";
  // Firm activities are firm-scoped categories; matter time uses job
  // categories. The category picker and sub-code picker follow the mode.
  const activeCategories = isFirm
    ? (context?.firmActivities ?? [])
    : (context?.categories ?? []);
  const selectedJob = context?.jobs.find((j) => j._id === form.jobId);
  const selectedJobStatus = selectedJob?.status;

  // Default the project code from the matter's phase. Keyed on the matter so
  // it only fires when that changes and a manual override afterwards sticks.
  useEffect(() => {
    if (!selectedJobStatus) return;
    if (parsedProjectCode.current) {
      // Skip once for the prefilled matter, then behave normally so a later
      // change of matter still picks up that matter's phase.
      parsedProjectCode.current = null;
      return;
    }
    setForm((prev) => ({
      ...prev,
      projectCode: selectedJobStatus === "pre_appointment" ? "pre" : "post",
    }));
  }, [form.jobId, selectedJobStatus]);

  const jobOptions = useMemo(() => {
    if (!context) return [];
    const recentRank = new Map(
      context.recentJobIds.map((id, i) => [id as string, i]),
    );
    return [...context.jobs]
      .sort((a, b) => {
        const ra = recentRank.get(a._id) ?? Infinity;
        const rb = recentRank.get(b._id) ?? Infinity;
        if (ra !== rb) return ra - rb;
        return a.name.localeCompare(b.name);
      })
      .map((j) => ({
        value: j._id as string,
        label: j.jobCode ? `${j.jobCode} — ${j.name}` : j.name,
        title: j.name,
      }));
  }, [context]);

  // What the parser could not work out. Recomputed against the live form so
  // the notice clears as each field is filled in rather than nagging.
  const needsAttention = useMemo(() => {
    const flagged = quickEntryPrefill?.unresolved ?? [];
    if (flagged.length === 0) return [];
    const stillMissing: Record<string, boolean> = {
      matter: !form.jobId,
      category: !form.categoryId,
      units: !form.units.trim(),
    };
    // An unrecognised field name stays flagged — better a stale hint than a
    // silently dropped one.
    return flagged.filter((field) => stillMissing[field] ?? true);
  }, [quickEntryPrefill, form.jobId, form.categoryId, form.units]);

  // A part unit rounds to a whole unit in the firm's direction (MOR-1016).
  const unitsNumber = roundUnits(Number(form.units), context?.roundingMode);
  // Rounding down can take a part unit to 0, which the server rejects; keep
  // Save disabled rather than surfacing that as an error toast.
  const unitsValid =
    Number.isFinite(unitsNumber) &&
    unitsNumber >= MIN_UNITS &&
    unitsNumber <= MAX_UNITS;
  // Dictation appends to the narrative (MOR-1456); the transcript is still
  // arriving while the mic is open, so saving waits for it.
  const dictation = useDictatedField({
    value: form.narrative,
    onChange: (next) => setForm((prev) => ({ ...prev, narrative: next })),
  });
  // Require a matter that is actually in the list — a stale or inaccessible
  // id would otherwise pass the check here and fail server-side on save.
  // Firm time carries no matter, so only the activity (category) is required.
  const canSave =
    (isFirm || !!selectedJob) &&
    !!form.categoryId &&
    !!form.date &&
    !!form.narrative.trim() &&
    unitsValid &&
    !dictation.voice.isActive;

  const handleSave = async (addAnother: boolean) => {
    if (!canSave || saving) return;
    setSaving(true);
    try {
      const units = unitsNumber;
      await createEntry({
        // Firm time has no matter or project code and is never billable; the
        // server ignores billable/projectCode for a firm-scoped category.
        ...(isFirm
          ? { billable: false }
          : {
              jobId: form.jobId as Id<"jobs">,
              billable: form.billable,
              projectCode: form.projectCode,
            }),
        date: form.date,
        startTime: form.startTime || undefined,
        endTime: effectiveEndTime || undefined,
        categoryId: form.categoryId as Id<"timeCategories">,
        subCategory: form.subCategory.trim() || undefined,
        narrative: form.narrative.trim(),
        units,
      });
      toast.success(
        `${units} ${units === 1 ? "unit" : "units"} saved as a draft entry.`,
      );
      if (addAnother) {
        // Keep the matter, date, category and flags — only the work changes.
        setForm((prev) => ({
          ...prev,
          units: "",
          narrative: "",
          subCategory: "",
          // The next piece of work happened in its own window, if any.
          startTime: "",
          endTime: "",
        }));
        // The next entry rolls on again (the day window re-reads).
        setStartTouched(false);
        setEndTouched(false);
        setShowTimes(showTimeOfDay);
        unitsRef.current?.focus();
      } else if (!advanceQuickEntry()) {
        closeQuickEntry();
      }
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not save the entry."));
    } finally {
      setSaving(false);
    }
  };

  // Closing mid-queue throws away entries the practitioner never saw, so say
  // so rather than letting them vanish.
  const handleClose = () => {
    if (quickEntryRemaining > 0) {
      toast.info(
        `${quickEntryRemaining} parsed ${quickEntryRemaining === 1 ? "entry was" : "entries were"} discarded.`,
      );
    }
    closeQuickEntry();
  };

  if (!orgId) return null;

  const hoursHint = unitsValid
    ? `${((unitsNumber * unitMinutes) / 60).toFixed(2)} h`
    : null;
  // The end of a timed entry is its start plus its units unless the person
  // typed one (or the stopwatch measured one).
  const effectiveEndTime =
    !endTouched && form.startTime && unitsValid
      ? addMinutesToClock(form.startTime, unitsNumber * unitMinutes)
      : form.endTime;

  return (
    <Dialog
      open={quickEntryOpen}
      onOpenChange={(next) => {
        if (!next) handleClose();
      }}
    >
      <DialogContent
        className="max-w-lg"
        onKeyDown={(e) => {
          // Cmd/Ctrl+Enter saves from any field, including the narrative
          // textarea where a bare Enter has to insert a newline.
          if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
            e.preventDefault();
            void handleSave(false);
          }
        }}
      >
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-2">
            Log time
            {quickEntryRemaining > 0 && (
              <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-normal text-muted-foreground">
                {quickEntryRemaining} more from your quick entry
              </span>
            )}
          </DialogTitle>
          <DialogDescription>
            Logged timesheets will be saved as a draft and can be submitted for
            approval in Timesheets.
          </DialogDescription>
        </DialogHeader>

        {!context ? (
          <div className="flex justify-center py-10">
            <Spinner />
          </div>
        ) : context.categories.length === 0 &&
          context.firmActivities.length === 0 ? (
          <div className="space-y-3 py-6 text-center">
            <p className="text-sm text-muted-foreground">
              Your firm has no time categories yet, so entries cannot be coded.
            </p>
            <Button variant="outline" size="sm" asChild>
              <Link
                to="/dashboard/timesheet-settings"
                onClick={closeQuickEntry}
              >
                Set up time categories
              </Link>
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {needsAttention.length > 0 && (
              <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
                Could not work out the {needsAttention.join(", ")} from what you
                typed — please fill{" "}
                {needsAttention.length === 1 ? "it" : "them"} in.
              </p>
            )}

            {/*
              Matter time is billed against a job; firm time (marketing, admin,
              training) counts toward hours but is never rated. The toggle only
              appears once a firm has set up activities.
            */}
            {context.firmActivities.length > 0 && (
              <Tabs
                value={form.mode}
                onValueChange={(value) =>
                  setForm((prev) => ({
                    ...prev,
                    mode: value as EntryMode,
                    // Category lists differ per mode, so a leftover id would
                    // point at a category the new mode never offers.
                    categoryId: "",
                    subCategory: "",
                  }))
                }
              >
                <TabsList className="w-full">
                  <TabsTrigger value="matter" className="flex-1">
                    Matter
                  </TabsTrigger>
                  <TabsTrigger value="firm" className="flex-1">
                    Firm activity
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            )}

            {!isFirm && (
              <div className="space-y-1.5">
                <Label htmlFor="quick-entry-matter">Matter</Label>
                <SearchableSelect
                  value={form.jobId}
                  onValueChange={(value) =>
                    setForm((prev) => ({ ...prev, jobId: value }))
                  }
                  options={jobOptions}
                  placeholder="Select matter…"
                  searchPlaceholder="Search matters…"
                  emptyMessage="No matters found."
                  className="w-full"
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Date</Label>
                <DatePicker
                  value={form.date}
                  onChange={(isoDate) =>
                    setForm((prev) => ({ ...prev, date: isoDate ?? "" }))
                  }
                  placeholder="Date"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="quick-entry-units">
                  Units{" "}
                  <span className="font-normal text-muted-foreground">
                    ({unitMinutes} min each)
                  </span>
                </Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="quick-entry-units"
                    ref={unitsRef}
                    type="number"
                    min={MIN_UNITS}
                    max={MAX_UNITS}
                    step={1}
                    value={form.units}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, units: e.target.value }))
                    }
                    placeholder="Units"
                    className="tabular-nums"
                  />
                  {hoursHint && (
                    <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                      {hoursHint}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/*
              Units are the billable duration; this window only records when
              the work happened. It stays collapsed and empty unless the
              stopwatch measured it, so nobody is nudged into guessing.
            */}
            {showTimes ? (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Start</Label>
                  <TimePicker
                    value={form.startTime || undefined}
                    onChange={(value) => {
                      setStartTouched(true);
                      setForm((prev) => ({ ...prev, startTime: value }));
                    }}
                    placeholder="Start time"
                    clearable
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>End</Label>
                  <TimePicker
                    value={effectiveEndTime || undefined}
                    onChange={(value) => {
                      setEndTouched(true);
                      setForm((prev) => ({ ...prev, endTime: value }));
                    }}
                    placeholder="End time"
                    clearable
                  />
                </div>
              </div>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                className="-ml-2 h-7 text-xs text-muted-foreground"
                onClick={() => setShowTimes(true)}
              >
                <Clock className="size-3.5" />
                Add start and end time
              </Button>
            )}

            <div className="space-y-1.5">
              <Label>{isFirm ? "Activity" : "Category"}</Label>
              <SearchableSelect
                value={form.categoryId}
                onValueChange={(value) =>
                  setForm((prev) => ({
                    ...prev,
                    categoryId: value,
                    // Sub-codes belong to one head category, so a leftover
                    // value would be filed under a category that never had it.
                    subCategory: "",
                  }))
                }
                options={activeCategories.map((c) => ({
                  value: c._id as string,
                  label: c.name,
                  leading: <CategoryGlyph color={c.color} />,
                }))}
                placeholder={isFirm ? "Activity…" : "Category…"}
                searchPlaceholder={
                  isFirm ? "Search activities…" : "Search categories…"
                }
                emptyMessage={
                  isFirm ? "No activities found." : "No categories found."
                }
                className="w-full"
              />
              <SubCategoryPicker
                categoryId={form.categoryId as Id<"timeCategories"> | ""}
                categories={activeCategories}
                value={form.subCategory}
                onChange={(next) =>
                  setForm((prev) => ({ ...prev, subCategory: next }))
                }
                className="w-full"
              />
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="quick-entry-narrative">Narrative</Label>
                <DictationButton voice={dictation.voice} />
              </div>
              <Textarea
                id="quick-entry-narrative"
                ref={narrativeRef}
                value={dictation.displayValue}
                readOnly={dictation.readOnly}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, narrative: e.target.value }))
                }
                placeholder={
                  dictation.voice.isListening
                    ? "Listening — start speaking…"
                    : "What did you work on?"
                }
                rows={3}
                {...NARRATIVE_INPUT_PROPS}
              />
              <NarrativeWarningNote
                narrative={form.narrative}
                units={Number(form.units)}
                unitMinutes={unitMinutes}
                orgId={orgId}
              />
            </div>

            {/* Firm time is never rated or billed, so it carries no billable
                flag or project code. */}
            {!isFirm && (
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  <Switch
                    id="quick-entry-billable"
                    checked={form.billable}
                    onCheckedChange={(checked) =>
                      setForm((prev) => ({ ...prev, billable: checked }))
                    }
                  />
                  <Label htmlFor="quick-entry-billable">Billable</Label>
                </div>
                <Select
                  value={form.projectCode}
                  onValueChange={(value) =>
                    setForm((prev) => ({
                      ...prev,
                      projectCode: value as ProjectCode,
                    }))
                  }
                >
                  <SelectTrigger className="w-28">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="post">POST</SelectItem>
                    <SelectItem value="pre">PRE</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        )}

        {!!context &&
          (context.categories.length > 0 ||
            context.firmActivities.length > 0) && (
            <DialogFooter className="sm:justify-between">
              <Button
                variant="ghost"
                size="sm"
                disabled={!canSave || saving}
                onClick={() => void handleSave(true)}
              >
                Save and add another
              </Button>
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={handleClose}>
                  Cancel
                </Button>
                <Button
                  disabled={!canSave || saving}
                  onClick={() => void handleSave(false)}
                >
                  {saving && <Loader2 className="size-4 animate-spin" />}
                  {quickEntryRemaining > 0 ? "Save and next" : "Save entry"}
                </Button>
              </div>
            </DialogFooter>
          )}
      </DialogContent>
    </Dialog>
  );
}
